import { Plugin, ButtonView, Dialog, DomEventObserver, WidgetToolbarRepository, isWidget } from 'ckeditor5';
import MathEditing from './MathEditing';
import MathFormView from './MathFormView';
import { loadMathLive } from './loadMathLive';
import { parseMath } from './mathMarkup';

/*
 * The equation editor: a toolbar button ("math"), a widget toolbar button
 * ("mathEdit") and double-click all open a dialog with MathLive's visual
 * math field. Saving converts MathLive's LaTeX to MathML for display.
 */

const ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">'
    + '<path d="M2.5 10.5h2.4l2.2 5 3.8-12.5H17v1.5h-4.9l-4.2 13.5H6.6l-2.6-6H2.5z"/>'
    + '<path d="M11.3 9.5h1.7l1.2 1.9 1.2-1.9h1.7l-2 3 2.1 3.2h-1.7l-1.3-2.1-1.3 2.1h-1.7l2.1-3.2z"/></svg>';

class DoubleClickObserver extends DomEventObserver {
    constructor(view) {
        super(view);
        this.domEventType = 'dblclick';
    }

    onDomEvent(domEvent) {
        this.fire(domEvent.type, domEvent);
    }
}

const isMathWidget = (viewElement) => !!viewElement && isWidget(viewElement) && viewElement.hasClass('ck-math');

export default class MathUI extends Plugin {
    static get pluginName() {
        return 'MathUI';
    }

    static get requires() {
        return [MathEditing, Dialog, WidgetToolbarRepository];
    }

    init() {
        const editor = this.editor;
        const t = editor.locale.t;
        const command = editor.commands.get('insertMath');
        this._loadToken = null;

        editor.ui.componentFactory.add('math', (locale) => {
            const button = new ButtonView(locale);
            button.set({ label: t('Equation'), icon: ICON, tooltip: true, isToggleable: true });
            button.bind('isEnabled').to(command);
            button.bind('isOn').to(command, 'value', (value) => !!value);
            this.listenTo(button, 'execute', () => this.openDialog());
            return button;
        });

        editor.ui.componentFactory.add('mathEdit', (locale) => {
            const button = new ButtonView(locale);
            button.set({ label: t('Edit equation'), withText: true, tooltip: true });
            button.bind('isEnabled').to(command);
            this.listenTo(button, 'execute', () => this.openDialog());
            return button;
        });

        const view = editor.editing.view;
        view.addObserver(DoubleClickObserver);
        this.listenTo(view.document, 'dblclick', (evt, data) => {
            const target = data.target;
            const widget = target && [target, ...target.getAncestors().reverse()].find(isMathWidget);
            if (!widget || !command.isEnabled) return;
            const element = editor.editing.mapper.toModelElement(widget);
            if (!element) return;
            editor.model.change((writer) => writer.setSelection(element, 'on'));
            data.preventDefault();
            this.openDialog();
        });
    }

    afterInit() {
        const widgetToolbars = this.editor.plugins.get(WidgetToolbarRepository);
        widgetToolbars.register('math', {
            ariaLabel: this.editor.locale.t('Equation toolbar'),
            items: ['mathEdit'],
            getRelatedElement: (selection) => {
                const element = selection.getSelectedElement();
                return isMathWidget(element) ? element : null;
            },
        });
    }

    /** Opens the equation dialog for the selected equation, or for a new one. */
    async openDialog() {
        const editor = this.editor;
        const t = editor.locale.t;
        const command = editor.commands.get('insertMath');
        if (!command.isEnabled) return;

        const dialog = editor.plugins.get(Dialog);
        const current = command.value;
        const form = new MathFormView(editor.locale);
        form.isBlock = !!current && current.display === 'block';
        form.status = t('Loading the equation editor…');
        let mathlive = null;

        const save = () => {
            const field = form.field;
            if (!field || !mathlive) return;
            const latex = field.value.trim();
            if (!latex) {
                if (current) editor.execute('insertMath', { remove: true });
                dialog.hide();
                return;
            }
            const data = parseMath(mathlive.convertLatexToMathMl(latex));
            if (!data) {
                form.status = t('This equation could not be converted. Try simplifying it.');
                return;
            }
            editor.execute('insertMath', { mathml: data.mathml, latex, display: form.isBlock ? 'block' : 'inline' });
            dialog.hide();
        };

        const token = {};
        this._loadToken = token;
        dialog.show({
            id: 'math',
            title: current ? t('Edit equation') : t('Insert equation'),
            className: 'ck-math-dialog',
            isModal: true,
            content: form.views,
            actionButtons: [
                { label: t('Cancel'), withText: true, onExecute: () => dialog.hide() },
                { label: t('Save'), class: 'ck-button-action', withText: true, onExecute: save },
            ],
            onHide: () => {
                if (this._loadToken === token) this._loadToken = null;
                editor.editing.view.focus();
            },
        });

        try {
            mathlive = await loadMathLive();
        } catch (err) {
            if (this._loadToken === token) form.status = t('The equation editor could not be loaded. Check your connection and try again.');
            return;
        }
        // The dialog may have been closed (or reopened) while MathLive loaded.
        if (this._loadToken !== token) return;

        const field = new mathlive.MathfieldElement();
        field.mathVirtualKeyboardPolicy = 'auto';
        field.value = current ? current.latex : '';
        form.setField(field);
        form.status = current && !current.latex
            ? t('This equation has no editable source. Saving replaces it with what you enter here.')
            : '';
        form.focus();
    }
}
